/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BR_25_ANY_CONDITION_VALIDATION
 * Date:         2026-02-06
 * Modified By:  Cursor
 *
 * Description:
 * BR-25: Added conditional validation for "Any Condition" - only allowed for WTB.
 * WTS broadcasts cannot use "Any Condition".
 */

import * as Yup from "yup";
import { CATEGORY_OPTIONS, CONDITION_OPTIONS } from "../../../../constants/inventoryOptions";
import { CURRENCIES } from "../../../../interfaces/Currency";

const itemValidationSchema = Yup.object().shape({
  manufacturer: Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .min(2, "Manufacturer must be at least 2 characters")
    .max(80, "Manufacturer must be 80 characters or less")
    .required("Manufacturer is required"),
  partNumber: Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .max(80, "Part number must be 80 characters or less")
    .test(
      "partNumber-or-model",
      "Either partNumber or model must be provided",
      function (value) {
        const model = (this.parent as any)?.model;
        const hasPart = typeof value === "string" && value.trim().length > 0;
        const hasModel = typeof model === "string" && model.trim().length > 0;
        return hasPart || hasModel;
      }
    ),
  model: Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .max(80, "Model must be 80 characters or less"),
  quantity: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .min(1, "Quantity must be at least 1")
    .integer("Quantity must be a whole number")
    .required("Quantity is required"),
  // [2026-02-06] BR-25: Conditional validation - "Any Condition" only for WTB
  condition: Yup.string()
    .required("Condition is required")
    .test(
      "valid-condition-for-type",
      "Any Condition is only allowed for WTB broadcasts",
      function (value) {
        if (!value) return true; // Let required handle empty
        // Access the broadcast type from the form root via this.from
        // this.from[0] = broadcast item, this.from[1] = root form
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const formType = (this.from as any)?.[1]?.value?.type;
        if (value === "Any Condition" && formType === "WTS") {
          return false;
        }
        return [...CONDITION_OPTIONS].includes(value as typeof CONDITION_OPTIONS[number]);
      }
    ),
  price: Yup.number().min(0, "Price cannot be negative"),
  currency: Yup.string()
    .oneOf([...CURRENCIES], "Invalid currency")
    .required("Currency is required"),
  categories: Yup.array()
    .of(Yup.string().oneOf([...CATEGORY_OPTIONS], "Invalid category"))
    .max(3, "Maximum 3 categories allowed")
    .optional()
    .nullable()
    .test("category-review", "Please select valid categories", function (value) {
      const item = this.parent;
      if (item.categoryNeedsReview) {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return this.createError({ message: "Please select at least one valid category" });
        }
      }
      return true;
    }),
  description: Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .max(50, "Description must be 50 characters or less"),
  country: Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .max(3, "Country must be 3 characters or less"),
});

export const validationSchema = Yup.object().shape({
  type: Yup.string().oneOf(["WTB", "WTS"], "Type must be WTB or WTS").required("Type is required"),
  broadcast: Yup.array().of(itemValidationSchema).min(1, "At least one broadcast is required"),
  items: Yup.array().of(itemValidationSchema).min(1, "At least one broadcast is required"),
});
